import { css } from '@emotion/react';
import { t } from 'i18n';
import { BoundConnection, PrepaidAncillaries, SpecialAssistance } from 'models';
import { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';

import { AirportInformation } from '../AirportInformation';
import { Divider } from '../Divider';
import { ErrorMessage } from '../ErrorMessage';
import { FlightDateAndTimeWithDuration } from '../FlightDateAndTimeWithDuration';
import { getServiceClassColor } from '../flights';
import { CheckboxField } from '../forms';
import { SasIcon } from '../SasIcon';
import { theme } from '../theme';

import { BoundStops } from './BoundStops';
import { ConditionList, FeeStatus } from './ConditionsList';

const styles = {
  flightContainer: (isSelected?: boolean, isRebookable?: boolean) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: ${theme.spacing.lg};
    border-radius: ${theme.borders.radius.lg};
    position: relative;
    gap: ${theme.spacing.md};
    outline: ${theme.colors.gray80} solid ${theme.borders.widths.sm};
    background: ${!isRebookable && theme.colors.white};
    cursor: ${!isRebookable && 'default'};
    outline: ${isSelected && `${theme.borders.widths.md} solid ${theme.colors.blue40}`};
    background: ${isRebookable ? theme.colors.white : theme.colors.gray[0]};
  `,
  bonusSymbol: css`
    display: inline-block;
    width: 12px;
    height: 12px;
    background-color: ${theme.colors.background.ebBonusGold};
    border-radius: ${theme.borders.radius.lg};
  `,
  wrapperBonusSymbol: css`
    padding-left: ${theme.spacing.xs};
  `,
  notRebookableTextContainer: css`
    display: flex;
  `,
  notRebookableText: css`
    color: var(--color-error);
    margin: 0;
    padding-left: 6px;
  `,
  boundTitle: css`
    display: flex;
    align-items: center;
    h4 {
      margin: 0;
      font-size: ${theme.fontSize.md};
      padding-right: ${theme.spacing.xs};
    }
  `,
  serviceClass: (backgroundColor: string | undefined, sarTextColor: string | undefined) => css`
    display: flex;
    position: absolute;
    right: 0;
    top: 0;
    border-radius: 0 12px;
    color: ${sarTextColor};
    background-color: ${backgroundColor ?? ''};
    align-items: center;
    padding: 12px 26px;

    @media (max-width: ${theme.breakpoints.tablet}) {
      padding: 4px 12px;
    }
  `,
  productType: css`
    font-weight: ${theme.fontWeight.bold};
    font-size: ${theme.fontSize.sm};
    margin: 0;
    margin-right: ${theme.spacing.xs};
  `,
};

const ServiceClass = ({ serviceClassName, isSar }: { serviceClassName?: string; isSar: boolean }) => {
  const { backgroundColor, color: textColor } = getServiceClassColor(serviceClassName);

  const sarTextColor =
    isSar && !serviceClassName?.toUpperCase().includes('GO') && !serviceClassName?.toUpperCase().includes('ECONOMY')
      ? theme.colors.white
      : textColor;

  return (
    <div css={styles.serviceClass(backgroundColor, sarTextColor)}>
      <p css={styles.productType}>{serviceClassName ?? ''}</p>
      {isSar && (
        <>
          <div css={styles.wrapperBonusSymbol}>
            <span css={styles.bonusSymbol}></span>
          </div>
        </>
      )}
    </div>
  );
};

interface FlightCardProps {
  bound: BoundConnection;
  headingText: string;
  ancillaries: PrepaidAncillaries | undefined;
  ancillariesWarningMessage?: string;
  headingIcon: string;
  isRebookable: boolean;
  setSelected: (info: { bound: BoundConnection; isSelected: boolean; headingText: string }) => void;
  isSelected: boolean;
  specialAssistance: SpecialAssistance | undefined;
  isSar?: boolean;
  onImpression?: (info: { bound: BoundConnection; headingText: string }) => void;
  cancellationFee?: FeeStatus;
  rebookingFee?: FeeStatus;
}

export const FlightCard = ({
  bound,
  headingText,
  headingIcon,
  ancillaries,
  setSelected,
  isSelected,
  isRebookable,
  specialAssistance,
  isSar = false,
  onImpression,
  cancellationFee = FeeStatus.UNKNOWN,
  rebookingFee = FeeStatus.UNKNOWN,
  ancillariesWarningMessage,
}: FlightCardProps) => {
  const handleOnChange = () => {
    setSelected({ bound, headingText, isSelected: !isSelected });
  };

  useEffect(() => {
    onImpression?.({ bound, headingText });
  }, []);
  return (
    <div css={styles.flightContainer(isSelected, isRebookable)}>
      <ServiceClass serviceClassName={bound.serviceClassName} isSar={isSar} />
      {!isRebookable && (
        <div css={styles.notRebookableTextContainer}>
          <SasIcon name="cl--alert-circle" />
          <h4 css={styles.notRebookableText}>{t('notRebookable')}</h4>
        </div>
      )}
      <div css={styles.boundTitle}>
        <h4>{headingText}</h4>
        <div>
          <SasIcon name={headingIcon} />
        </div>
      </div>

      <CheckboxField
        disabled={!isRebookable}
        onChange={handleOnChange}
        checked={isSelected}
        name={headingText}
        forceOnlyText={true}
      >
        <div>
          {bound && bound.origin && bound.destination && (
            <AirportInformation
              originCityName={bound.origin.cityName}
              originAirportCode={bound.origin.airportCode}
              destinationCityName={bound.destination.cityName}
              destinationAirportCode={bound.destination.airportCode}
            />
          )}
          <div>
            <FlightDateAndTimeWithDuration
              flightSegments={bound.segmentCount}
              departureTimeLocal={bound.startTimeLocal}
              arrivalTimeLocal={bound.endTimeLocal}
              departureTimeGMT={bound.startTimeGMT}
              arrivalTimeGMT={bound.endTimeGMT}
              shouldShowStops={true}
            />
          </div>

          <BoundStops segmentCount={bound.segmentCount} segments={bound.flightSegments} />
        </div>
      </CheckboxField>

      {isRebookable && (
        <>
          <Divider />
          <div>
            <ConditionList
              cancellationFee={cancellationFee}
              rebookingFee={rebookingFee}
              prepaidBaggage={ancillaries?.baggage}
              specialAssistance={specialAssistance}
            />
          </div>
        </>
      )}

      {ancillariesWarningMessage && <ErrorMessage highlighted message={ancillariesWarningMessage} />}
    </div>
  );
};

export const FlightCardSkeleton = () => {
  return (
    <div css={[styles.flightContainer(false, true), { gap: '18px' }]}>
      <div
        css={css`
          position: absolute;
          top: 0;
          right: 0;
        `}
      >
        <Skeleton
          css={css`
            display: inline-block;
            width: 115px;
            height: 45px;
            border-radius: 0 ${theme.borders.radius.lg};
          `}
        />
      </div>
      <Skeleton width={90} height={15} />
      <div
        css={css`
          display: flex;
          gap: ${theme.spacing.sm};
          width: 100%;
        `}
      >
        <Skeleton width={16} />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing.xs};
            min-width: 0;
          `}
        >
          <Skeleton
            css={css`
              max-width: 100%;
              width: 340px;
            `}
          />
          <Skeleton
            css={css`
              max-width: 100%;
              width: 340px;
            `}
          />
        </div>
      </div>
      <Divider />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing.sm};
          width: 100%;
        `}
      >
        <Skeleton width={140} />
        <div
          css={css`
            display: flex;
            gap: ${theme.spacing.sm};

            > :last-child {
              min-width: 0;
            }
          `}
        >
          <Skeleton width={16} height={16} circle />
          <Skeleton
            css={css`
              max-width: 100%;
              width: 340px;
            `}
          />
        </div>
      </div>
    </div>
  );
};
